import React from "react";
import { Icon, EmptyState, notify, confirmDialog } from "./registry";
/* ============================================================
   Fence Works — Business line (docs/VOIP_PLAN.md; migration #48)
   The number a brand publishes and what happens when it's called or texted:
   who rings (screened, with a whisper), business hours, voicemail with a
   transcript or the AI receptionist when nobody picks up, texts relayed to
   the owner's phones. The service does the work (server/src/voice/**); this
   screen edits the line's routing document and shows the calls and
   voicemails it took. Without a live service it says so.
   ============================================================ */

const DAYS: [string, string][] = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];
const OUTCOME_LABEL: any = { ringing: "Ringing", answered: "Answered", ai: "AI receptionist", voicemail: "Voicemail", missed: "Missed", failed: "Failed" };
const pretty = (e: string) => { const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(String(e || "")); return m ? `(${m[1]}) ${m[2]}-${m[3]}` : String(e || ""); };
const when = (at: any) => { const d = new Date(at); return isNaN(d.getTime()) ? "" : d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); };
const dur = (s: number) => { s = Math.max(0, Math.round(Number(s) || 0)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };
const dbOf = (): any => (window as any).VFP_DB;

function Toggle({ on, onChange, label, hint, disabled }: any) {
  return (
    <label className="toggle-row" style={{ opacity: disabled ? 0.55 : 1 }} onClick={() => { if (!disabled) onChange(!on); }}>
      <span className={"sw" + (on ? " on" : "")}><span className="knob" /></span>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{label}{hint && <span className="faint" style={{ fontWeight: 400, marginLeft: 8 }}>{hint}</span>}</span>
    </label>
  );
}

/* the routing editor: a draft of the line's routing, saved as one document */
function RoutingEditor({ line, data, onSaved }: any) {
  const DB = dbOf();
  const [r, setR] = React.useState<any>(() => JSON.parse(JSON.stringify(line.routing || {})));
  const [dirty, setDirty] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { setR(JSON.parse(JSON.stringify(line.routing || {}))); setDirty(false); }, [line.id, line.updated_at]);
  const set = (fn: (d: any) => void) => { setR((cur: any) => { const d = JSON.parse(JSON.stringify(cur)); fn(d); return d; }); setDirty(true); };
  const ring = r.ring || {}, vm = r.voicemail || {}, sms = r.sms || {}, hours = r.hours || {};
  const targets: any[] = ring.targets || [];
  const ai = !!data.aiAvailable;

  async function save() {
    setBusy(true);
    try { const out = await DB.lineRouting(line.id, r); notify("Saved. Calls follow the new routing right away."); setDirty(false); onSaved(out); }
    catch (e: any) { notify(String(e.message || e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="card pad" style={{ marginTop: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <h2 className="display" style={{ fontSize: 18, margin: 0 }}>How calls to {pretty(line.e164)} are handled</h2>
        <button className="btn btn-primary" disabled={!dirty || busy} onClick={save}><Icon name="check" size={14} />{busy ? "Saving…" : "Save routing"}</button>
      </div>

      <h3 style={{ fontSize: 14, margin: "18px 0 6px" }}>Who answers</h3>
      <p className="muted" style={{ fontSize: 12.5, margin: "0 0 10px" }}>These phones ring. Whoever picks up hears the whisper and presses 1 to take the call, so a cell phone's own voicemail can't grab it.</p>
      {targets.map((t: any, i: number) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, marginBottom: 8 }}>
          <input className="input" placeholder="Label (Rob's cell)" value={t.label || ""} onChange={(e) => set((d) => { d.ring.targets[i].label = e.target.value; })} />
          <input className="input" placeholder="(920) 555-0101" value={t.e164 || ""} onChange={(e) => set((d) => { d.ring.targets[i].e164 = e.target.value; })} />
          <button className="btn" onClick={() => set((d) => { d.ring.targets.splice(i, 1); })} title="Remove"><Icon name="trash" size={14} /></button>
        </div>
      ))}
      {targets.length < 5 && <button className="btn" onClick={() => set((d) => { d.ring = d.ring || {}; d.ring.targets = [...(d.ring.targets || []), { label: "", e164: "" }]; })}><Icon name="plus" size={14} />Add a phone</button>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginTop: 12 }}>
        <div className="ai-field"><label className="field-label">Ring</label>
          <select className="select" value={ring.mode || "simultaneous"} onChange={(e) => set((d) => { d.ring.mode = e.target.value; })}>
            <option value="simultaneous">All phones at once</option><option value="sequential">One after another</option>
          </select></div>
        <div className="ai-field"><label className="field-label">Seconds to ring {ring.mode === "sequential" ? "each" : ""}</label>
          <input className="input" type="number" min={5} max={60} value={ring.timeoutSec ?? 20} onChange={(e) => set((d) => { d.ring.timeoutSec = Number(e.target.value); })} /></div>
        <div className="ai-field"><label className="field-label">Whisper (what you hear first)</label>
          <input className="input" placeholder={`${line.brand || "Business"} call`} value={ring.whisper || ""} onChange={(e) => set((d) => { d.ring.whisper = e.target.value; })} /></div>
      </div>
      <Toggle on={targets.length > 1 ? true : ring.screen !== false} disabled={targets.length > 1} label="Press 1 to accept" hint={targets.length > 1 ? "always on when more than one phone rings" : "off: the first phone to answer, voicemail included, takes the call"} onChange={(v: boolean) => set((d) => { d.ring.screen = v; })} />

      <h3 style={{ fontSize: 14, margin: "18px 0 6px" }}>Business hours <span className="faint" style={{ fontWeight: 400 }}>({r.tz || data.defaults?.tz || "your time zone"})</span></h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 8 }}>
        {DAYS.map(([k, label]) => {
          const v = hours[k];
          const open = Array.isArray(v);
          return (
            <div key={k} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, width: 86, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                <input type="checkbox" checked={open} onChange={(e) => set((d) => { d.hours = d.hours || {}; d.hours[k] = e.target.checked ? ["07:00", "17:00"] : null; })} />{label}
              </label>
              {open ? (<>
                <input className="input" type="time" value={v[0]} onChange={(e) => set((d) => { d.hours[k][0] = e.target.value; })} style={{ padding: "4px 6px" }} />
                <span className="faint">to</span>
                <input className="input" type="time" value={v[1]} onChange={(e) => set((d) => { d.hours[k][1] = e.target.value; })} style={{ padding: "4px 6px" }} />
              </>) : <span className="faint" style={{ fontSize: 12.5 }}>closed</span>}
            </div>
          );
        })}
      </div>

      <h3 style={{ fontSize: 14, margin: "18px 0 6px" }}>When nobody picks up</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
        <div className="ai-field"><label className="field-label">During business hours</label>
          <select className="select" value={r.onNoAnswer || "voicemail"} onChange={(e) => set((d) => { d.onNoAnswer = e.target.value; })}>
            <option value="ai" disabled={!ai}>AI receptionist{ai ? "" : " (turn it on in AI Front Desk)"}</option><option value="voicemail">Voicemail</option><option value="hangup">Say we'll call back and hang up</option>
          </select></div>
        <div className="ai-field"><label className="field-label">After hours</label>
          <select className="select" value={r.afterHours || "voicemail"} onChange={(e) => set((d) => { d.afterHours = e.target.value; })}>
            <option value="ai" disabled={!ai}>AI receptionist{ai ? "" : " (turn it on in AI Front Desk)"}</option><option value="voicemail">Voicemail</option>
          </select></div>
      </div>
      <p className="faint" style={{ fontSize: 11.5, margin: "6px 0 0" }}>Every missed call and voicemail becomes a customer with a "Call back" task, and the missed-call text-back goes out from your registered texting number — this one, when this is the number you adopted.</p>

      <h3 style={{ fontSize: 14, margin: "18px 0 6px" }}>Voicemail</h3>
      <div className="ai-field"><label className="field-label">Greeting (read aloud)</label>
        <textarea className="input" rows={2} placeholder={`Thanks for calling ${line.brand || "us"}. We can't take your call right now. Leave your name, number and what you need after the tone, and we'll call you back.`} value={vm.greetingText || ""} onChange={(e) => set((d) => { d.voicemail = d.voicemail || {}; d.voicemail.greetingText = e.target.value; })} /></div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
        <div className="ai-field"><label className="field-label">Max length (seconds)</label>
          <input className="input" type="number" min={10} max={180} value={vm.maxSec ?? 120} onChange={(e) => set((d) => { d.voicemail = d.voicemail || {}; d.voicemail.maxSec = Number(e.target.value); })} /></div>
        <div className="ai-field"><label className="field-label">Tell me by</label>
          <div style={{ display: "flex", gap: 12, paddingTop: 6 }}>
            {["push", "sms", "email"].map((n) => (
              <label key={n} style={{ fontSize: 13, display: "flex", gap: 5, alignItems: "center", cursor: "pointer" }}>
                <input type="checkbox" checked={(vm.notify || []).includes(n)} onChange={(e) => set((d) => { d.voicemail = d.voicemail || {}; const cur = d.voicemail.notify || []; d.voicemail.notify = e.target.checked ? [...cur, n] : cur.filter((x: string) => x !== n); })} />{n === "push" ? "App notification" : n === "sms" ? "Text" : "Email"}
              </label>
            ))}
          </div></div>
      </div>
      <Toggle on={vm.transcribe !== false} label="Transcribe voicemails" hint="the words land on the customer's card and in the email" onChange={(v: boolean) => set((d) => { d.voicemail = d.voicemail || {}; d.voicemail.transcribe = v; })} />

      <h3 style={{ fontSize: 14, margin: "18px 0 6px" }}>Texts to this number</h3>
      <Toggle on={sms.inbox !== false} label="Show them in Messages" onChange={(v: boolean) => set((d) => { d.sms = d.sms || {}; d.sms.inbox = v; })} />
      <div className="ai-field" style={{ marginTop: 8 }}><label className="field-label">Also relay each text to these phones</label>
        <input className="input" placeholder="(920) 555-0101, (920) 555-0102" value={(sms.relayTo || []).join(", ")} onChange={(e) => set((d) => { d.sms = d.sms || {}; d.sms.relayTo = e.target.value.split(",").map((x: string) => x.trim()).filter(Boolean); })} />
        <p className="faint" style={{ fontSize: 11.5, margin: "4px 0 0" }}>Relayed texts arrive from Fence Works' alert number. Reply from Messages so the customer sees your business number.</p></div>
      <div className="ai-field"><label className="field-label">After-hours auto-reply (blank = none)</label>
        <input className="input" placeholder="Thanks! We're closed until 7am and will text you back then." value={sms.afterHoursAutoReply || ""} onChange={(e) => set((d) => { d.sms = d.sms || {}; d.sms.afterHoursAutoReply = e.target.value; })} /></div>

      <h3 style={{ fontSize: 14, margin: "18px 0 6px" }}>Recording</h3>
      <Toggle on={!!(r.record && r.record.live)} label="Record answered calls" hint="off by default; callers must be told where the law requires it" onChange={(v: boolean) => set((d) => { d.record = { live: v }; })} />
    </div>
  );
}

function VoicemailRow({ vm }: any) {
  const DB = dbOf();
  const [url, setUrl] = React.useState<string>("");
  const [heard, setHeard] = React.useState(!!vm.listened_at);
  async function play() {
    try { const r = await DB.voicemailAudio(vm.id); setUrl(r.url); if (!heard) { DB.voicemailListened(vm.id).catch(() => {}); setHeard(true); } }
    catch (e: any) { notify(String(e.message || e)); }
  }
  return (
    <div style={{ padding: "10px 0", borderTop: "1px solid var(--line)" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        {!heard && <span className="chip teal">New</span>}
        <b>{pretty(vm.from_number) || "Unknown"}</b>
        <span className="faint">{when(vm.created_at)} · {dur(vm.duration_sec)}</span>
        {!url && <button className="btn" onClick={play} disabled={!vm.storage_path} title={vm.storage_path ? "" : "The recording is still on its way"}><Icon name="bolt" size={13} />Listen</button>}
      </div>
      {url && <audio controls autoPlay src={url} style={{ width: "100%", marginTop: 8 }} />}
      {vm.transcript && <p className="muted" style={{ fontSize: 13, margin: "6px 0 0" }}>"{vm.transcript}"</p>}
    </div>
  );
}

function PhonePage({ store, go }: any) {
  const DB = dbOf();
  const live = !!(DB && DB.apiBase && DB.apiBase() && DB.lines);
  const [data, setData] = React.useState<any>(null);
  const [err, setErr] = React.useState<any>(null);
  const [busy, setBusy] = React.useState<string>("");
  const [areaCode, setAreaCode] = React.useState("920");
  const [testTo, setTestTo] = React.useState("");

  const load = React.useCallback(async () => {
    if (!live) return;
    try { setErr(null); setData(await DB.lines()); }
    catch (e: any) { setErr(e); }
  }, [live]);
  React.useEffect(() => { load(); }, [load]);

  async function act(key: string, fn: () => Promise<any>, done?: string) {
    setBusy(key);
    try { const out = await fn(); if (out && out.lines) setData(out); else await load(); if (done) notify(done); }
    catch (e: any) { notify(String(e.message || e)); }
    finally { setBusy(""); }
  }

  if (!live) {
    return (
      <div className="page fade-up">
        <h1 className="display" style={{ fontSize: 26, margin: "0 0 6px" }}>Business line</h1>
        <EmptyState icon="phone" title="Needs the live service" body="Your business number, who it rings, voicemail and texting are set up on app.fenceworks.ai." />
      </div>
    );
  }
  if (err) {
    const plan = err.code === "plan" || err.status === 403;
    return (
      <div className="page fade-up">
        <h1 className="display" style={{ fontSize: 26, margin: "0 0 6px" }}>Business line</h1>
        <div className="card pad"><p className="muted" style={{ margin: 0 }}>{plan ? "A business line is on the Pro plan and up." : String(err.message || err)}</p>
          {plan && <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={() => go("account")}>See plans</button>}</div>
      </div>
    );
  }
  if (!data) return <div className="page fade-up"><h1 className="display" style={{ fontSize: 26 }}>Business line</h1><p className="muted">Loading…</p></div>;

  const lines: any[] = data.lines || [];
  const reg = data.registration;
  return (
    <div className="page fade-up" style={{ maxWidth: 920 }}>
      <h1 className="display" style={{ fontSize: 26, margin: "0 0 6px" }}>Business line</h1>
      <p className="muted" style={{ fontSize: 14, margin: "0 0 16px", maxWidth: 680 }}>One number for your website, Google profile and print. It rings the phones you choose, takes a voicemail with a transcript or hands the call to your AI receptionist, and texts come from the same number the customer called.</p>

      {!data.setup && <div className="pe-callout warn"><p className="muted" style={{ margin: 0 }}>{`Needs a database update (migration #48, ${data.migration}). Ask your admin to run it.`}</p></div>}
      {data.setup && !data.configured && <div className="pe-callout warn"><p className="muted" style={{ margin: 0 }}>The phone provider isn't configured on this server yet (Twilio credentials and a public service URL).</p></div>}

      {data.setup && !lines.length && (
        <div className="card pad">
          <h2 className="display" style={{ fontSize: 18, margin: "0 0 8px" }}>Get your number</h2>
          {reg && reg.number && (
            <div className="pe-callout info" style={{ marginBottom: 12 }}>
              <p className="muted" style={{ margin: "0 0 8px", fontSize: 13 }}>Your registered texting number <b style={{ color: "var(--fg)" }}>{pretty(reg.number)}</b> can be your business line: the same number starts taking calls, and texts keep going out under your campaign.</p>
              <button className="btn btn-primary" disabled={!!busy || !data.configured} onClick={() => act("adopt", () => DB.lineAdopt({}), `${pretty(reg.number)} is now your business line.`)}><Icon name="phone" size={14} />{busy === "adopt" ? "Setting up…" : `Use ${pretty(reg.number)} as my business line`}</button>
            </div>
          )}
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div className="ai-field" style={{ margin: 0 }}><label className="field-label">Area code</label><input className="input" style={{ width: 110 }} value={areaCode} onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, "").slice(0, 3))} /></div>
            <button className="btn" disabled={!!busy || !data.configured} onClick={() => act("buy", () => DB.lineBuy({ areaCode }), "Your new business line is live.")}><Icon name="plus" size={14} />{busy === "buy" ? "Buying…" : "Buy a new number"}</button>
            <span className="faint" style={{ fontSize: 12 }}>about ${data.monthly}/month, on us while you're on Pro or above</span>
          </div>
        </div>
      )}

      {lines.map((line: any) => (
        <div key={line.id}>
          <div className="card pad">
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
              <div>
                <div className="faint" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: ".06em" }}>{line.brand || "Business"} · {line.source === "adopted" ? "your registered texting number" : "purchased"}</div>
                <div className="display" style={{ fontSize: 30, margin: "2px 0" }}>{line.e164 ? pretty(line.e164) : "Setting up…"}</div>
                <span className={"chip " + (line.status === "active" ? "teal" : "")}>{line.status}</span>
                {line.error && <span className="faint" style={{ marginLeft: 8, color: "var(--red)" }}>{line.error}</span>}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {line.e164 && <button className="btn" onClick={() => { navigator.clipboard?.writeText(pretty(line.e164)); notify("Copied."); }}><Icon name="clipboard" size={14} />Copy</button>}
                <button className="btn" disabled={!!busy} onClick={async () => {
                  const ok = await confirmDialog({ title: `Release ${pretty(line.e164)}?`, body: line.source === "adopted" ? "Calls to it stop being handled here. It stays your registered texting number, so texts keep working." : "The number goes back to the phone provider and can't be recovered. Anyone who saved it gets a dead line.", confirmLabel: "Release" });
                  if (ok) act("release:" + line.id, () => DB.lineRelease(line.id), "Released.");
                }}><Icon name="trash" size={14} />Release</button>
              </div>
            </div>
            <p className="muted" style={{ fontSize: 12.5, margin: "10px 0 0" }}>Put this number on your website, your Google Business Profile (primary phone), your truck and your cards. Keep your old number as Google's "additional phone" for a few months so listings still match.</p>
            {line.status === "active" && (
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", marginTop: 12 }}>
                <div className="ai-field" style={{ margin: 0 }}><label className="field-label">Test it: ring this phone from the line</label><input className="input" style={{ width: 200 }} placeholder={((line.routing || {}).ring || {}).targets?.[0]?.e164 || "(920) 555-0101"} value={testTo} onChange={(e) => setTestTo(e.target.value)} /></div>
                <button className="btn" disabled={!!busy} onClick={() => act("test:" + line.id, () => DB.lineTestCall(line.id, testTo || ((line.routing || {}).ring || {}).targets?.[0]?.e164), "Calling you now from the line.")}><Icon name="phone" size={14} />Call me</button>
              </div>
            )}
          </div>
          <RoutingEditor line={line} data={data} onSaved={(out: any) => { if (out && out.lines) setData(out); }} />
        </div>
      ))}

      {data.setup && (data.voicemails || []).length > 0 && (
        <div className="card pad" style={{ marginTop: 14 }}>
          <h2 className="display" style={{ fontSize: 18, margin: "0 0 4px" }}>Voicemails</h2>
          {(data.voicemails || []).map((vm: any) => <VoicemailRow key={vm.id} vm={vm} />)}
        </div>
      )}

      {data.setup && (data.calls || []).length > 0 && (
        <div className="card pad" style={{ marginTop: 14 }}>
          <h2 className="display" style={{ fontSize: 18, margin: "0 0 8px" }}>Recent calls</h2>
          <table className="table" style={{ width: "100%", fontSize: 13 }}>
            <thead><tr><th>When</th><th>From</th><th>Outcome</th><th>Taken by</th><th>Length</th></tr></thead>
            <tbody>
              {(data.calls || []).map((c: any) => (
                <tr key={c.id}><td>{when(c.started_at)}</td><td>{pretty(c.from_number) || "Unknown"}</td><td>{OUTCOME_LABEL[c.outcome] || c.outcome}</td><td>{c.answered_by === "ai" ? "AI receptionist" : c.answered_by === "voicemail" ? "Voicemail" : pretty(c.answered_by) || "—"}</td><td>{c.duration_sec ? dur(c.duration_sec) : "—"}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {((data.ai || []).length > 0 || (data.tracking || []).length > 0) && (
        <div className="card pad" style={{ marginTop: 14 }}>
          <h2 className="display" style={{ fontSize: 18, margin: "0 0 8px" }}>Your other numbers</h2>
          {(data.ai || []).map((a: any, i: number) => <p key={"ai" + i} className="muted" style={{ margin: "4px 0", fontSize: 13 }}><b style={{ color: "var(--fg)" }}>{pretty(a.number)}</b> — AI receptionist{a.vertical && a.vertical !== "fence" ? ` (${a.vertical})` : ""}. Never published; your business line falls through to it. <a href="#/ai" onClick={(e) => { e.preventDefault(); go("ai"); }}>AI Front Desk</a></p>)}
          {(data.tracking || []).map((t: any) => <p key={t.id} className="muted" style={{ margin: "4px 0", fontSize: 13 }}><b style={{ color: "var(--fg)" }}>{pretty(t.e164)}</b> — website call tracking, forwards to your published number. <a href="#/website" onClick={(e) => { e.preventDefault(); go("website"); }}>Website</a></p>)}
        </div>
      )}
    </div>
  );
}

export { PhonePage };
